// Reply to Message Component
// Following existing patterns from MessageComposer

import React, { useState, useRef, useCallback } from 'react';
import { ReplyToMessageProps } from '../../../types/messaging';
import {
  ReplyContainer,
  ReplyHeader,
  ReplyOriginalMessage,
  ReplyContent,
  ReplyActions,
  ReplyButton,
  CancelButton
} from '../../../styles/Messaging/MessageActions.styles';

export const ReplyToMessage: React.FC<ReplyToMessageProps> = ({
  originalMessage,
  onSendReply,
  onCancel,
  disabled = false
}) => {
  const [replyText, setReplyText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleReplyChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReplyText(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  const handleSendReply = useCallback(async () => {
    if (!replyText.trim()) return;
    if (disabled) return;

    try {
      await onSendReply(replyText.trim(), originalMessage.id);
      setReplyText('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
    }
  }, [replyText, originalMessage.id, onSendReply, disabled]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  }, [handleSendReply, onCancel]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <ReplyContainer>
      <ReplyHeader>
        <span>Replying to {originalMessage.senderName}</span>
        <CancelButton onClick={onCancel}>✕</CancelButton>
      </ReplyHeader>
      
      <ReplyOriginalMessage>
        <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
          {formatTime(originalMessage.createdAt)}
        </div>
        <div style={{ fontSize: '0.9rem' }}>
          {originalMessage.content}
        </div>
      </ReplyOriginalMessage>
      
      <ReplyContent>
        <textarea
          ref={textareaRef}
          value={replyText}
          onChange={handleReplyChange}
          onKeyDown={handleKeyDown}
          placeholder="Type your reply..."
          disabled={disabled}
          rows={2}
          style={{
            width: '100%',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '0.75rem',
            fontSize: '0.9rem',
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit'
          }}
        />
      </ReplyContent>
      
      <ReplyActions>
        <CancelButton onClick={onCancel}>
          Cancel
        </CancelButton>
        <ReplyButton
          onClick={handleSendReply}
          disabled={!replyText.trim() || disabled}
        >
          Reply
        </ReplyButton>
      </ReplyActions>
    </ReplyContainer>
  );
};
